import React, {
    FocusEventHandler,
    KeyboardEventHandler,
    useEffect,
    useRef,
    useState,
} from 'react';
import SearchIcon from '../svg/SearchIcon';
import CrossIcon from '../svg/CrossIcon';

export type AppSearchBarCallables = {
    setValue: (value: string) => void;
};

type AppSearchBarProps = {
    id?: string;
    name?: string;
    isEnabled?: boolean;
    placeholder: string;
    setSearchQuery: Function;
    className?: string;
    inputRef?: any;
    inputMode?: 'text' | 'numeric';
    maxLength?: number;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    onSubmit?: () => void;
    setCallables?: (callables: AppSearchBarCallables) => void;
};

const AppSearchBar: React.FC<AppSearchBarProps> = ({
    id,
    className,
    name,
    isEnabled = true,
    placeholder,
    setSearchQuery,
    inputRef,
    inputMode,
    maxLength,
    onBlur,
    onFocus,
    onKeyDown,
    onSubmit,
    setCallables,
}) => {
    const defaultRef = useRef<HTMLInputElement>(null);
    const ref = inputRef ?? defaultRef;

    const [showResetButton, setShowResetButton] = useState(false);

    useEffect(() => {
        if (setCallables) {
            setCallables({
                setValue,
            });
        }
    }, [setCallables]);

    const handleChange = (e: any) => {
        setShowResetButton(ref?.current?.value);
        setSearchQuery(e.target.value);
    };

    const onFormSubmit = (e: any) => {
        e.preventDefault();
    };

    const setValue = (value: string) => {
        if (ref.current) {
            ref.current.value = value;
        }
    };

    const resetValue = () => {
        if (ref.current && isEnabled) {
            ref.current.value = '';
            setShowResetButton(false);
            handleChange({ target: { value: '' } });
        }
    };

    return (
        <form onSubmit={onFormSubmit} className={className}>
            <div className="relative">
                <div className="absolute inset-y-0 right-0 flex justify-end">
                    {showResetButton && (
                        <button
                            type="button"
                            className="focus:outline-none focus:shadow-outline"
                            onClick={resetValue}
                        >
                            <span className="flex items-center justify-center w-11 h-full">
                                <CrossIcon className="text-primary-1 -scale-x-100" />
                            </span>
                        </button>
                    )}
                </div>
                <div className="absolute inset-y-0 left-0 flex justify-end">
                    <button
                        type="submit"
                        className="focus:outline-none focus:shadow-outline"
                        onClick={onSubmit}
                    >
                        <span
                            className={`flex items-center justify-center w-11 h-full pl-6`}
                        >
                            <SearchIcon
                                className={`${
                                    isEnabled
                                        ? 'text-primary-1'
                                        : 'text-gray-500'
                                }`}
                            />
                        </span>
                    </button>
                </div>
                <input
                    id={id}
                    ref={ref}
                    type="search"
                    onChange={handleChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    name={name}
                    maxLength={maxLength}
                    inputMode={inputMode ?? 'text'}
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    disabled={!isEnabled}
                    className="w-full min-h-[2.5rem] focus:border-mid-blue pl-14 px-4 py-3 rounded-full placeholder-primary-2 text-primary-1 border-gray-200"
                />
            </div>
        </form>
    );
};

export default AppSearchBar;
